import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/city/content-template'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'

import Amenities from '../amenities'
import RentingIn from '../renting-in'

const Kaslo = ({
  filterSlug,
  isCanadaPage,
  cityName,
  cityNameWithState,
  cityData,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const links = getFilterLinks(isCanadaPage, citySlug)

  const BcNeighbourhoodsLink =
    'https://liv.rent/blog/neighbourhoods/british-columbia-neighbourhoods/'

  const cheapNeighbourhoodsLink = isChinese
    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e7%a7%9f%e6%88%bf%e5%ae%9d%e5%85%b8-%e5%a4%a7%e6%b8%a9%e5%93%a5%e5%8d%8e%e7%a7%9f%e9%87%91%e6%9c%80%e4%be%bf%e5%ae%9c%e7%9a%8410%e4%b8%aa%e5%9c%b0%e5%8c%ba/'
    : 'https://liv.rent/blog/neighbourhoods/2020-renting-in-canada-what-1800-gets-you-across-canada/'

  const getAbout = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.kaslo:About Kaslo Paragraph 1">
          Kaslo, nestled along the eastern shore of Kootenay Lake in
          southeastern{' '}
          <LinkText href="/rental-listings/british-columbia">
            British Columbia (BC)
          </LinkText>
          , Canada, is a picturesque village celebrated for its natural splendor
          and vibrant arts community. Surrounded by the majestic Selkirk and
          Purcell Mountains, it offers a paradise for outdoor enthusiasts, with
          hiking, biking, fishing, and more in the pristine wilderness. Kaslo’s
          annual Jazz Etc. Summer Music Festival draws music aficionados from
          near and far. The village boasts a lively cultural scene, with
          galleries, studios, and heritage buildings, such as the SS Moyie
          sternwheeler. Kaslo’s strong sense of community shines through its
          friendly residents and regular gatherings. Amidst the serene lakeside
          setting, Kaslo is a hidden gem among the diverse array of{' '}
          <LinkText href={BcNeighbourhoodsLink}>
            cities and neighbourhoods in BC
          </LinkText>
          , welcoming those seeking natural beauty, culture, and a close-knit
          community.
        </Trans>
      </Paragraph>
    )
  }

  const getRentingIn = () => {
    return (
      <RentingIn
        filterSlug={filterSlug}
        cityName={cityName}
        rentingTipsLink={cheapNeighbourhoodsLink}
      />
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        isCanadaPage={isCanadaPage}
        cityName={cityName}
        cityNameWithState={cityNameWithState}
        citySlug={citySlug}
        rentAmounts={{
          bed1: '$1000 - $2,000',
          bed2: '$1,300 - $2,300',
          bed3: '$2,000 - $3,500'
        }}
        rentReportCity={t('cities:city_name_vancouver')}
        rentReportLink="https://liv.rent/blog/rent-reports/vancouver/"
        rentReportLinkZh="https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/%E6%B8%A9%E5%93%A5%E5%8D%8E/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.kaslo:Apartment Hunting in Kaslo Paragraph">
            Compared to cities like{' '}
            <LinkText href="/rental-listings/city/vancouver">
              Vancouver
            </LinkText>{' '}
            and{' '}
            <LinkText href="/rental-listings/city/richmond">Richmond</LinkText>,
            it’s easier to find{' '}
            <LinkText href={links.under1500}>cheap rentals in Kaslo</LinkText> –
            although{' '}
            <LinkText href={links.under1000}>
              apartments for rent under $1000
            </LinkText>{' '}
            are still uncommon.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo:Apartment Hunting in LOCATION Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available in {{ cityName }} (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.kaslo:Apartments for rent Kaslo Paragraph">
              In Kaslo,{' '}
              <LinkText href={links.apartment}>apartments for rent</LinkText>{' '}
              often appeal to those looking for practicality and convenience.
              These rental units, which can vary from older, more historic
              buildings to newer, modernized structures, are typically favored
              by singles, couples, and small families. They offer the advantage
              of being close to the village’s amenities, including local shops
              and cafes, all set against the backdrop of the stunning Kootenay
              landscape.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.kaslo:Houses for rent Kaslo Paragraph">
              <LinkText href={links.house}>Houses for rent in Kaslo</LinkText>{' '}
              provide a more traditional living experience. These homes range
              from heritage properties that echo the village’s rich history to
              newer, contemporary styles. Often accompanied by gardens or
              outdoor spaces, they are perfect for families or individuals who
              appreciate a bit more space and a connection to the quiet, natural
              beauty of the area.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.kaslo:Townhouses for rent Kaslo Paragraph">
              <LinkText href={links.townhouse}>
                Townhouses for rent in Kaslo
              </LinkText>{' '}
              offer a middle ground between apartments and detached houses.
              Preferred by small families, couples, or professionals, townhouses
              typically provide multiple levels of living space, along with some
              outdoor area like a small yard or patio, combining the convenience
              of compact living with a sense of privacy and community.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.kaslo:Basements for rent Kaslo Paragraph">
              Basements for rent in Kaslo are a budget-friendly option, ideal
              for individuals or young professionals. These units, usually part
              of a larger house, offer affordable living spaces. While they
              might have limited natural light, they often provide a cozy,
              intimate setting nestled in the quiet Kaslo environment.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.kaslo:Condos for rent Kaslo Paragraph">
              <LinkText href={links.apartment}>
                Condos for rent in Kaslo
              </LinkText>{' '}
              cater to those seeking a low-maintenance lifestyle with a sense of
              community. These units, which can range from modest to more
              luxurious, are particularly appealing to retirees or small
              families. Condos in Kaslo often come with the added benefit of
              communal spaces and are situated to take advantage of the area’s
              scenic beauty.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.kaslo:Rooms for rent Kaslo Paragraph">
              <LinkText href={links.room}>Rooms for rent in Kaslo</LinkText> are
              the most accessible and flexible option, commonly chosen by
              students, seasonal workers, or individuals seeking short-term
              accommodations. Rooms for rent usually involve sharing common
              areas such as the kitchen and living room, offering a communal
              living experience that’s both economical and sociable.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals CITY Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ cityName }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Keep in mind that some {{ cityName }} neighbourhoods are better
              suited to those with pets, and adjust your search accordingly.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals LOCATION Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ cityName }}. Renters seeking furnished units
              should search for these units using filters, but do be prepared to
              pay more in rent. There are{' '}
              <LinkText href="https://liv.rent/blog/renters/renting-furnished-unfurnished/">
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.kaslo:Short term rentals Kaslo Paragraph">
              Kaslo, a small village in the West Kootenay region of British
              Columbia, offers limited employment opportunities, mainly in
              sectors like tourism, small business, and services, reflective of
              its size and rural nature. It is not known for a wide range of
              academic institutions, being a small community rather than a major
              educational hub. In Kaslo, the demand for{' '}
              <LinkText href={links.shortTerm}>short-term rentals</LinkText> is
              more prominent during the tourist season, particularly in the
              summer months when visitors are drawn to its natural beauty and
              heritage charm. This seasonal pattern of short-term rental demand
              is different from larger cities or university towns, where such
              rentals are popular year-round, especially at the start of school
              semesters. The{' '}
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                flexibility of short-term rentals is an attractive feature for
                many
              </LinkText>
              , especially those seeking temporary accommodation for leisure or
              seasonal activities in the area.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.kaslo:Cheap rentals Kaslo Paragraph">
              If you’re looking for{' '}
              <LinkText href={links.under1000}>cheap rentals in Kaslo</LinkText>
              , try finding a roommate or two and explore shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
              It’s worth noting that in Kaslo, there are neighbourhoods where
              rent is considerably cheaper, so exploring these options is a good
              idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getLiving = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.kaslo:Living in Kaslo Paragraph">
            Located in the stunning wilderness of British Columbia, Kaslo is a
            hidden gem that offers a unique blend of natural beauty and
            small-town charm. Situated in the Kootenay region, this picturesque
            town is surrounded by magnificent mountains, lush forests, and
            crystal-clear lakes. Renowned for its tight-knit community and
            welcoming atmosphere, Kaslo provides a plethora of attractions and
            amenities for residents and tourists alike. Outdoor enthusiasts can
            indulge in a wide range of activities, including hiking, fishing,
            and kayaking, while culture aficionados can immerse themselves in
            the local arts scene and historical landmarks. With its serene
            ambiance and unhurried pace of life, Kaslo beckons as an idyllic
            haven for those seeking a tranquil escape in the heart of nature.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.city.kaslo:Living in Kaslo Paragraph 2">
            If you’re not sure that Kaslo is the right city for you, check out
            some of these resources comparing the different cities.
          </Trans>
        </Paragraph>
        {isChinese ? (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e5%8a%a0%e6%8b%bf%e5%a4%a7%e7%95%99%e5%ad%a65%e5%a4%a7%e5%9f%8e%e5%b8%82-%e7%a7%9f%e6%88%bf%e3%80%81%e5%ad%a6%e8%b4%b9%e3%80%81%e5%b7%a5%e4%bd%9c%e3%80%81%e7%ad%be%e8%af%81%e8%af%a6%e8%a7%a3/">
              加拿大最佳移民城市
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e6%b8%a9%e5%93%a5%e5%8d%8e%e3%80%81%e5%a4%9a%e4%bc%a6%e5%a4%9a%e6%af%94%e8%be%83/">
              温哥华 vs 多伦多：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%E8%A1%97%E5%8C%BA%E6%8C%87%E5%8D%97/%E6%B8%A9%E5%93%A5%E5%8D%8E%E5%92%8C%E7%B4%A0%E9%87%8C%E5%B1%85%E4%BD%8F%E6%88%90%E6%9C%AC%E6%AF%94%E8%BE%83/">
              温哥华 vs 素里：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/toronto-zh/2021%e7%89%88%e5%a4%9a%e4%bc%a6%e5%a4%9a%e3%80%81%e8%92%99%e7%89%b9%e5%88%a9%e5%b0%94%e7%94%9f%e6%b4%bb%e6%88%90%e6%9c%ac%e6%af%94%e8%be%83/">
              多伦多 vs 蒙特利尔：生活成本比较
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/2020/11/best-canadian-cities-for-immigration/">
              Best Canadian cities for immigration
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2019/11/vancouver-or-toronto-a-cost-of-living-comparison/">
              Vancouver vs Toronto: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2021/02/surrey-vs-vancouver-bc-comparison/">
              Vancouver vs Surrey: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2021/04/toronto-vs-montreal-cost-of-living-comparison/">
              Toronto vs Montreal: Cost of living comparison
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getPopularHoods = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.kaslo:Popular Neighbourhoods Kaslo Paragraph">
          Like any city, Kaslo is made up of smaller neighbourhoods with their
          own distinct features. Some of the most popular neighbourhoods include
          Downtown Kaslo, Kaslo Bay, Kaslo River Road, Upper Kaslo, and Kaslo
          Golf Course. For more info about Kaslo, you can consult this guide on
          BC neighbourhoods.{' '}
          <LinkText href={BcNeighbourhoodsLink}>BC neighbourhoods</LinkText>.
        </Trans>
      </Paragraph>
    )
  }

  const getLandmarks = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.kaslo:Local Landmarks Kaslo Paragraph">
          Kaslo, BC, is a town renowned for its captivating landmarks that
          highlight its natural splendor and historical significance. One such
          landmark is the Kaslo River Trail, a picturesque walking path that
          winds alongside the serene Kaslo River. This trail, adorned with lush
          vegetation and scenic views, provides visitors with a serene escape
          into nature. Another notable landmark is the SS Moyie National
          Historic Site, a restored sternwheeler that offers a glimpse into the
          town’s maritime history. Visitors can explore the ship’s decks and
          learn about its role in transporting goods and passengers during the
          early 20th century. The Langham Cultural Centre is another must-visit
          landmark in Kaslo. Housed in a historic building, this center
          showcases local art, hosts cultural events, and preserves the town’s
          artistic heritage. Lastly, Kaslo’s vibrant downtown area, with its
          charming heritage buildings and eclectic shops, is a landmark in
          itself. Exploring the streets of downtown Kaslo is like stepping back
          in time, with its well-preserved architecture and unique small-town
          charm. These landmarks make Kaslo, BC, an enchanting destination that
          offers a perfect blend of natural beauty, cultural heritage, and
          small-town charm.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities cityName={cityName} filterSlug={filterSlug} links={links} />
    ),
    living: getLiving(),
    popularHoods: getPopularHoods(),
    landmarks: getLandmarks()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      isCanadaPage={isCanadaPage}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
    />
  )
}

export default Kaslo
